import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Stage, StageEvents, SubscribeType } from 'amazon-ivs-web-broadcast';
import streamApi from '../../services/streamApi';
import { refreshSpadesBalance } from '../../utils/spadesBalance';
import { readStoredUser } from '../../utils/navbarUser';

const POLL_MS = 3000;
const MAX_LIVE_VIEWERS = 3;

function fmtDuration(seconds) {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

// Streamer subscribes to all viewers, never publishes from this panel
const STREAMER_STRATEGY = {
  stageStreamsToPublish: () => [],
  shouldPublishParticipant: () => false,
  shouldSubscribeToParticipant: (participant) =>
    participant.isLocal ? SubscribeType.NONE : SubscribeType.AUDIO_VIDEO,
};

function VideoRequestPanel({ isLive, onViewerStreamsChange }, ref) {
  const [requests, setRequests] = useState([]);
  const [activeViewers, setActiveViewers] = useState([]);
  const [viewerSlotsOpen, setViewerSlotsOpen] = useState(false);

  const stageRef = useRef(null);
  // IVS participant.id → { stream: MediaStream, viewerId: string, requestId: string | null }
  const participantMediaRef = useRef(new Map());
  // viewer_id → { viewer_username, request_id }
  const viewerMetaRef = useRef(new Map());
  const pollRef = useRef(null);

  const currentUser = readStoredUser();
  const streamerId = currentUser?.id || '';

  const notifyParent = useCallback(() => {
    if (!onViewerStreamsChange) return;
    const streams = Array.from(participantMediaRef.current.values()).map(({ stream, viewerId }) => ({
      viewerId,
      username: viewerMetaRef.current.get(viewerId)?.viewer_username || viewerId,
      stream,
    }));
    onViewerStreamsChange(streams);
  }, [onViewerStreamsChange]);

  // ── IVS Stage ──────────────────────────────────────────────────────────────

  const leaveStage = useCallback(() => {
    if (stageRef.current) {
      try { stageRef.current.leave(); } catch {}
      stageRef.current = null;
    }
    participantMediaRef.current.clear();
    viewerMetaRef.current.clear();
    setActiveViewers([]);
    onViewerStreamsChange?.([]);
  }, [onViewerStreamsChange]);

  const joinStage = useCallback(async () => {
    if (stageRef.current) return;
    try {
      const { token } = await streamApi.getStreamerStageToken();
      const stage = new Stage(token, STREAMER_STRATEGY);

      stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant, streams) => {
        if (participant.isLocal) return;
        const viewerId = String(participant.attributes?.identity || participant.userId || '');
        const meta = viewerMetaRef.current.get(viewerId);
        const requestId = meta?.request_id || null;

        let entry = participantMediaRef.current.get(participant.id);
        if (!entry) {
          entry = { stream: new MediaStream(), viewerId, requestId };
          participantMediaRef.current.set(participant.id, entry);
        } else {
          entry.viewerId = viewerId;
          entry.requestId = requestId;
        }
        streams.forEach((s) => {
          try { entry.stream.addTrack(s.mediaStreamTrack); } catch {}
        });

        setActiveViewers((prev) => {
          const nextKey = requestId || `viewer:${viewerId}`;
          const existingIndex = prev.findIndex((viewer) => (viewer.request_id || `viewer:${viewer.viewer_id}`) === nextKey);
          if (existingIndex === -1) {
            return [
              ...prev,
              {
                viewer_id: viewerId,
                viewer_username: meta?.viewer_username || viewerId,
                request_id: requestId,
                participant_id: participant.id,
              },
            ];
          }

          const existing = prev[existingIndex];
          if (
            existing.viewer_id === viewerId
            && existing.viewer_username === (meta?.viewer_username || viewerId)
            && existing.request_id === requestId
            && existing.participant_id === participant.id
          ) {
            return prev;
          }

          const next = [...prev];
          next[existingIndex] = {
            ...existing,
            viewer_id: viewerId,
            viewer_username: meta?.viewer_username || viewerId,
            request_id: requestId,
            participant_id: participant.id,
          };
          return next;
        });
        notifyParent();
      });

      stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (participant, streams) => {
        const entry = participantMediaRef.current.get(participant.id);
        if (entry) {
          streams.forEach((s) => {
            try { entry.stream.removeTrack(s.mediaStreamTrack); } catch {}
          });
        }
        notifyParent();
      });

      stage.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant) => {
        const entry = participantMediaRef.current.get(participant.id);
        if (entry) {
          participantMediaRef.current.delete(participant.id);
          setActiveViewers((prev) => prev.map((viewer) => (
            viewer.participant_id === participant.id
              ? { ...viewer, participant_id: null }
              : viewer
          )));
        }
        notifyParent();
      });

      await stage.join();
      stageRef.current = stage;
    } catch (err) {
      // IVS_STAGE_ARN not configured — stage features unavailable in this env
      if (process.env.NODE_ENV !== 'production') {
        console.warn('IVS Stage join failed (expected when IVS_STAGE_ARN is not set):', err.message);
      }
    }
  }, [notifyParent]);

  useEffect(() => {
    if (!isLive || !streamerId) return undefined;
    joinStage();
    return () => {};
  }, [isLive, streamerId, joinStage]);

  useEffect(() => {
    if (isLive) return undefined;
    leaveStage();
    return undefined;
  }, [isLive, leaveStage]);

  // ── Poll for pending requests ─────────────────────────────────────────────

  useEffect(() => {
    if (!isLive || !streamerId) {
      clearInterval(pollRef.current);
      return undefined;
    }

    const poll = async () => {
      try {
        const data = await streamApi.getVideoRequestsForStreamer();
        const all = data.requests || [];
        setViewerSlotsOpen(Boolean(data.viewer_slots_open));
        setRequests(all.filter((r) => r.status === 'pending'));

        // Track viewer metadata so Stage participant events can map identity → username
        const approved = all.filter((r) => r.status === 'approved');
        const approvedBalanceRefreshKey = approved
          .map((request) => request.id)
          .filter(Boolean)
          .sort()
          .join('|');

        if (
          approvedBalanceRefreshKey
          && approvedBalanceRefreshKey !== approvedBalanceRefreshKeyRef.current
        ) {
          approvedBalanceRefreshKeyRef.current = approvedBalanceRefreshKey;
          void refreshSpadesBalance();
        }

        const approvedRequestIds = new Set();
        const nextViewerMeta = new Map();

        approved.forEach((r) => {
          approvedRequestIds.add(r.id);
          nextViewerMeta.set(r.viewer_id, {
            viewer_username: r.viewer_username,
            request_id: r.id,
          });
        });

        viewerMetaRef.current = nextViewerMeta;

        // Prune stale participant streams when the backend no longer reports an approved request.
        for (const [participantId, entry] of participantMediaRef.current) {
          const requestId = entry.requestId || nextViewerMeta.get(entry.viewerId)?.request_id || null;
          if (!approvedRequestIds.has(requestId)) {
            participantMediaRef.current.delete(participantId);
            continue;
          }
          entry.requestId = requestId;
        }

        const participantIdsByRequestId = new Map();
        for (const [participantId, entry] of participantMediaRef.current) {
          if (entry.requestId) {
            participantIdsByRequestId.set(entry.requestId, participantId);
          }
        }

        // Rebuild the active viewer list from the current approved requests so the latest poll
        // is the source of truth while preserving matching participants already in stage.
        setActiveViewers((prev) => {
          const prevByRequestId = new Map(prev.filter((viewer) => viewer.request_id).map((viewer) => [viewer.request_id, viewer]));
          const next = approved.map((request) => {
            const existing = prevByRequestId.get(request.id);
            const participantId = participantIdsByRequestId.get(request.id) || existing?.participant_id || null;
            return {
              viewer_id: request.viewer_id,
              viewer_username: request.viewer_username,
              request_id: request.id,
              participant_id: participantId,
            };
          });

          if (
            prev.length === next.length
            && prev.every((viewer, index) => (
              viewer.viewer_id === next[index].viewer_id
              && viewer.viewer_username === next[index].viewer_username
              && viewer.request_id === next[index].request_id
              && viewer.participant_id === next[index].participant_id
            ))
          ) {
            return prev;
          }

          return next;
        });

        notifyParent();
      } catch {}
    };

    poll();
    pollRef.current = setInterval(poll, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [isLive, streamerId]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const removeViewer = useCallback((viewerId, requestId = null) => {
    // Clean up IVS participant entry
    for (const [pid, entry] of participantMediaRef.current) {
      if ((requestId && entry.requestId === requestId) || entry.viewerId === viewerId) {
        participantMediaRef.current.delete(pid);
      }
    }
    viewerMetaRef.current.delete(viewerId);
    setActiveViewers((prev) => prev.filter((v) => v.request_id !== requestId && v.viewer_id !== viewerId));
    notifyParent();
  }, [notifyParent]);

  const handleReject = async (req) => {
    try {
      await streamApi.rejectVideoRequest(req.id);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
    } catch (err) {
      console.error('Reject video request failed:', err.message);
    }
  };

  const handleStop = async (viewer) => {
    try { await streamApi.endVideoRequest(viewer.request_id); } catch {}
    removeViewer(viewer.viewer_id, viewer.request_id);
  };

  const handleExtend = async (viewer, extraSeconds) => {
    try {
      await streamApi.extendVideoRequest(viewer.request_id, extraSeconds);
    } catch (err) {
      console.error('Extend failed:', err.message);
    }
  };

  // Expose stopViewer so GoLivePage can call it from the tile overlay button
  useImperativeHandle(ref, () => ({
    stopViewer: async (viewerId) => {
      const viewer = activeViewers.find((v) => v.viewer_id === viewerId);
      if (viewer) {
        try { await streamApi.endVideoRequest(viewer.request_id); } catch {}
        removeViewer(viewerId, viewer.request_id);
      }
    },
  }), [activeViewers, removeViewer]);

  if (!isLive) return null;

  const hasPending = requests.length > 0;
  const hasActive = activeViewers.length > 0;

  return (
    <div className="go-live-page__card vr-panel">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h3 className="go-live-page__card-title" style={{ margin: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          Viewer Video Queue
          {hasPending && <span className="go-live-page__queue-badge">{requests.length}</span>}
        </h3>
      </div>

      <p className="vr-panel__empty">
        {viewerSlotsOpen
          ? `Viewer participation is open in OBS. Up to ${MAX_LIVE_VIEWERS} viewers can auto-join, and extra paid viewers wait in FIFO order.`
          : 'Viewer participation is currently closed in OBS.'}
      </p>

      {!hasPending && !hasActive && (
        <p className="vr-panel__empty">No active viewers or queued requests right now.</p>
      )}

      {hasActive && (
        <div className="vr-panel__active-list">
          <p className="vr-panel__active-label">
            {activeViewers.length} viewer{activeViewers.length !== 1 ? 's' : ''} live in stream
          </p>
          {activeViewers.map((viewer) => (
            <div key={viewer.request_id || viewer.viewer_id} className="vr-panel__active-row">
              <div className="vr-panel__request-avatar">
                {viewer.viewer_username ? viewer.viewer_username.charAt(0).toUpperCase() : '?'}
              </div>
              <span className="vr-panel__request-name">{viewer.viewer_username}</span>
              <span className="vr-panel__pip-badge vr-panel__pip-badge--inline">LIVE</span>
              <div className="vr-panel__active-actions">
                <button
                  type="button"
                  className="go-live-page__queue-action go-live-page__queue-action--approve vr-panel__extend-btn"
                  onClick={() => handleExtend(viewer, 30)}
                  title="Extend by 30 seconds"
                >
                  +30s
                </button>
                <button
                  type="button"
                  className="go-live-page__queue-action go-live-page__queue-action--approve vr-panel__extend-btn"
                  onClick={() => handleExtend(viewer, 60)}
                  title="Extend by 1 minute"
                >
                  +1m
                </button>
                <button
                  type="button"
                  className="go-live-page__queue-action go-live-page__queue-action--reject vr-panel__stop-btn"
                  onClick={() => handleStop(viewer)}
                >
                  Stop
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasPending && (
        <div className="vr-panel__list">
          {requests.map((req, index) => (
            <div key={req.id} className="vr-panel__request-card">
              <div className="vr-panel__request-avatar">
                {req.viewer_username ? req.viewer_username.charAt(0).toUpperCase() : '?'}
              </div>
              <div className="vr-panel__request-info">
                <span className="vr-panel__request-name">{req.viewer_username}</span>
                <span className="vr-panel__request-label">
                  Queue #{req.queue_position || index + 1}
                  {` · ${fmtDuration(req.duration_seconds)}`}
                  {req.spade_cost ? ` · ${req.spade_cost.toLocaleString()} spades paid` : ''}
                </span>
              </div>
              <div className="vr-panel__request-actions">
                <button
                  type="button"
                  className="go-live-page__queue-action go-live-page__queue-action--reject"
                  onClick={() => handleReject(req)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VideoRequestPanelWithRef = forwardRef(VideoRequestPanel);

VideoRequestPanelWithRef.propTypes = {
  isLive: PropTypes.bool.isRequired,
  onViewerStreamsChange: PropTypes.func,
};

VideoRequestPanelWithRef.defaultProps = {
  onViewerStreamsChange: null,
};

export default VideoRequestPanelWithRef;
